// frontend/src/app/core/services/toc-api.service.ts

import {
  Injectable,
  Inject,
  PLATFORM_ID,
  Optional,
  PendingTasks,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, from, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import {
  ASSET_JSON_SERVER_READER,
  AssetJsonServerReader,
} from './asset-json.tokens';

export interface TocEntry {
  type: string;
  slug: string;
  toc_title: string;
  path_segments: string[];
  toc_order?: number;
  display_order?: number;
  title?: string;
}

@Injectable({ providedIn: 'root' })
export class TocApiService {
  private readonly isServer: boolean;
  private readonly useStatic = !!environment.useStaticContent;
  private readonly apiBase = (environment.apiBaseUrl || '').replace(/\/$/, '');

  // Cache the result so SSR + hydration + multiple subscribers don’t re-run the load.
  private toc$?: Observable<{ toc: TocEntry[] }>;

  constructor(
    private http: HttpClient,
    private pendingTasks: PendingTasks,
    @Inject(PLATFORM_ID) pid: object,
    @Optional()
    @Inject(ASSET_JSON_SERVER_READER)
    private assetReader?: AssetJsonServerReader,
  ) {
    this.isServer = isPlatformServer(pid);
  }

  get(): Observable<{ toc: TocEntry[] }> {
    if (!this.toc$) {
      this.toc$ = this.withPending(this.buildSource()).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.toc$;
  }

  // --------------------
  // Source selection
  // --------------------

  private buildSource(): Observable<{ toc: TocEntry[] }> {
    // Critical: during SSR/prerender, always read the static asset.
    // Do NOT attempt HTTP first on the server.
    if (this.isServer) {
      return this.loadStatic();
    }

    // Browser behavior: honor env flag, otherwise try API then fallback.
    if (this.useStatic) {
      return this.loadStatic();
    }

    const url = this.apiBase ? `${this.apiBase}/api/toc` : '/api/toc';
    return this.http.get<any>(url).pipe(
      map((raw) => this.mapToToc(raw)),
      catchError(() => this.loadStatic()),
      catchError((err) => {
        console.error('[TocApiService] TOC load failed (api + static).', err);
        return of({ toc: [] });
      }),
    );
  }

  // --------------------
  // Static loader
  // --------------------

  private loadStatic(): Observable<{ toc: TocEntry[] }> {
    if (this.isServer) {
      // Prefer filesystem-backed reader during SSR/prerender.
      if (this.assetReader) {
        return from(this.assetReader.readJson<any>('/assets/v1/toc.json')).pipe(
          map((raw) => this.mapToToc(raw)),
          catchError((err) => {
            console.error(
              '[TocApiService] failed to read /assets/v1/toc.json on server.',
              err,
            );
            return of({ toc: [] });
          }),
        );
      }

      // Last resort fallback (should be rare if server provider is registered).
      return from(import('@assets/v1/toc.json')).pipe(
        map((m: any) => this.mapToToc(m?.default ?? m)),
        catchError((err) => {
          console.error(
            '[TocApiService] failed to import @assets/v1/toc.json on server.',
            err,
          );
          return of({ toc: [] });
        }),
      );
    }

    // Browser: fetch from /assets
    return this.http.get<any>('assets/v1/toc.json').pipe(
      map((raw) => this.mapToToc(raw)),
      catchError((err) => {
        console.error(
          '[TocApiService] failed to fetch assets/v1/toc.json in browser.',
          err,
        );
        return of({ toc: [] });
      }),
    );
  }

  // --------------------
  // Prerender stability
  // --------------------

  private withPending<T>(obs: Observable<T>): Observable<T> {
    // PendingTasks blocks ApplicationRef.isStable until removed.
    const remove = this.pendingTasks.add();
    return obs.pipe(finalize(() => remove()));
  }

  // --------------------
  // Mapping
  // --------------------

  private mapToToc(raw: any): { toc: TocEntry[] } {
    const arr = Array.isArray(raw?.toc)
      ? raw.toc
      : Array.isArray(raw)
        ? raw
        : [];

    const toc = arr.map((e: any) => ({
      type: e?.type ?? 'condition',
      slug: e?.slug ?? '',
      toc_title: e?.toc_title ?? e?.title ?? '',
      path_segments: Array.isArray(e?.path_segments) ? e.path_segments : [],
      toc_order: e?.toc_order,
      display_order: e?.display_order,
      title: e?.title,
    })) as TocEntry[];

    return { toc };
  }
}
